import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { FETCH_CITY_TABLE_DATA, SET_COUNTRY_NAME } from "../../sysGuardConstants";
import { TablePagination } from "@mui/material";

function LocationAccordionTable({
  rows,
  columns,
  cityColumns,
  dataSourceId,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
}) {
  const dispatch = useDispatch();
  const allCityTableData = useSelector(
    (state) => state.SysGuardReducer.cityTableData || {},
  );

  const totalElements = useSelector(
    (state) => state.SysGuardReducer.totalElements,
  );

  const cityLevelTotalElements = useSelector(
    (state) => state.SysGuardReducer.cityLevelTotalElements,
  );


  const [expandedRow, setExpandedRow] = useState(null);
  const [cityPaginationModel, setCityPaginationModel] = useState({});

  const renderValue = (value) => {
    if (value === null || value === undefined) {
      return "-";
    }

    if (typeof value === "object") {
      return Object.entries(value)
        .map(([key, val]) => `${key}: ${val}`)
        .join(", ");
    }

    return value;
  };

  const paginatedRows = rows.slice(
    currentPage * pageSize,
    currentPage * pageSize + pageSize,
  );

  const handleAccordionChange = (row) => (event, isExpanded) => {
    const cityPage = cityPaginationModel[row.countryName]?.page || 0;
    const cityPageSize = cityPaginationModel[row.countryName]?.pageSize || 5;
    setExpandedRow(isExpanded ? row.id : null);

    if (isExpanded) {
      dispatch({
        type: SET_COUNTRY_NAME,
        payload: row.countryName,
      });
      dispatch({
        type: FETCH_CITY_TABLE_DATA,
        payload: {
          id: dataSourceId,
          currentPage: cityPage,
          pageSize: cityPageSize,
          countryName: row.countryName,
        },
      });
    }
  };

  return (
    <Box className="locationAccordionWrapper">
      {/* HEADER */}
      <Box
        className="locationAccordionHeader"
        sx={{
          gridTemplateColumns: `
      ${columns.map((column) => `${column.flex || 1}fr`).join(" ")}
      48px
    `,
        }}
      >
        {columns.map((column) => (
          <Box
            key={column.field}
            className={`locationAccordionHeaderCell ${
              column.headerAlign === "center" ? "headerCenterAlign" : ""
            }`}
          >
            {column.headerName}
          </Box>
        ))}

        {/* Empty column for accordion icon alignment */}
        <Box className="locationAccordionHeaderCell" />
      </Box>

      {/* ROWS */}
      <Box className="locationAccordionBody">
        {paginatedRows.map((row, index) => {
          const cityTableData = allCityTableData?.[row.countryName] || [];

          return (
            <Accordion
              key={row.id}
              expanded={expandedRow === row.id}
              onChange={handleAccordionChange(row)}
              disableGutters
              elevation={0}
              square
              className="locationAccordion"
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className="locationAccordionSummary"
              >
                <Box
                  className="locationAccordionRow"
                  sx={{
                    gridTemplateColumns: columns
                      .map((column) => `${column.flex || 1}fr`)
                      .join(" "),
                  }}
                >
                  {columns.map((column) => {
                    const value = row[column.field];

                    const params = {
                      row,
                      value,
                      field: column.field,
                      id: row.id,
                    };

                    return (
                      <Box
                        key={column.field}
                        className={`locationAccordionCell ${
                          column.align === "center" ? "centerAlign" : ""
                        } ${column.field}`}
                      >
                        {column.renderCell ? (
                          column.renderCell(params)
                        ) : (
                          <Typography variant="body2">
                            {renderValue(value)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </AccordionSummary>

              <AccordionDetails className="locationAccordionDetails">
                <DataGrid
                  rows={cityTableData}
                  columns={cityColumns}
                  pagination
                  paginationMode="server"
                  rowCount={cityLevelTotalElements || 0}
                  paginationModel={
                    cityPaginationModel[row.countryName] || {
                      page: 0,
                      pageSize: 5,
                    }
                  }
                  onPaginationModelChange={(model) => {
                    setCityPaginationModel((prev) => ({
                      ...prev,
                      [row.countryName]: model,
                    }));

                    dispatch({
                      type: FETCH_CITY_TABLE_DATA,
                      payload: {
                        id: dataSourceId,
                        currentPage: model.page,
                        pageSize: model.pageSize,
                        countryName: row.countryName,
                      },
                    });
                  }}
                  pageSizeOptions={[5, 10, 20, 50]}
                  disableRowSelectionOnClick
                  localeText={{
                    noRowsLabel: "No City Data Available",
                  }}
                  sx={{
                    border: "1px solid #e0e0e0",
                    height: 400,

                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#4d8b72",
                      color: "#fff",
                      fontWeight: 600,
                    },

                    "& .MuiDataGrid-main": {
                      overflow: "hidden",
                    },

                    "& .MuiDataGrid-virtualScroller": {
                      overflowY: "auto !important",
                    },
                  }}
                />
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* PAGINATION */}
      <Box className="locationAccordionPagination">
        <TablePagination
          component="div"
          count={totalElements}
          page={currentPage}
          onPageChange={(event, newPage) => {
            setCurrentPage(newPage);
          }}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => {
            setPageSize(parseInt(event.target.value, 10));
            setCurrentPage(0);
          }}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
        />
      </Box>
    </Box>
  );
}

export default LocationAccordionTable;
